import { useCoreService } from 'core/services/useCoreService';
import { createContext, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SearchCostCentreTotalsError } from '../models/errors/SearchCostCentreTotalsError';
import { SearchCostCentreTotalsRequest } from '../models/requests/SearchCostCentreTotalsRequest';
import { CentroCostoTotalsReport } from '../models/CentroCostoTotalsReport/CentroCostoTotalsReport';
import {
  COST_CENTRE_LIST_BY_PERIOD_PATH,
  COST_CENTRE_LIST_PATH,
  COST_CENTRE_TOTALS_BY_ID_PATH,
} from '../routes';
import { CARDS_TOTALS_BY_ACCOUNT_PATH } from 'core/tarjeta/routes';
import { COST_CENTRE_MESSAGES } from '../constants/CostCentreMessages';
import { formatParams } from 'shared/utils';
import { Periodo } from 'shared/models/Periodo';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { useNotifications } from 'shared/notifications';

interface ICostCentreTotalsContext {
  costCentreTotals: CentroCostoTotalsReport;
  searchParams: URLSearchParams;
  period: Periodo;
  currentCostCenterId: () => number;
  goBack: () => void;
  goToMovementsByCC: () => void;
  handleCostCenterRedirection: () => void;
}

export const CostCentreTotalsContext = createContext<ICostCentreTotalsContext>({
  costCentreTotals: null,
  searchParams: null,
  period: null,
  currentCostCenterId: () => 0,
  goBack: () => {},
  goToMovementsByCC: () => {},
  handleCostCenterRedirection: () => {},
});

export const CostCentreTotalsProvider = ({ children }) => {
  const [costCentreTotals, setCostCentreTotalsRequest] =
    useDataFetcher<CentroCostoTotalsReport>(null);
  const { ccId } = useParams();
  const coreService = useCoreService();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const period = new Periodo(searchParams.get('mes'), searchParams.get('anio'));
  const goBackPath =
    !period.isValid() || period.isCurrentPeriod()
      ? COST_CENTRE_LIST_PATH
      : COST_CENTRE_LIST_BY_PERIOD_PATH;

  useEffect(() => {
    const params = new SearchCostCentreTotalsRequest({
      costCentreId: Number.parseInt(ccId),
      soloHijos: Boolean(searchParams.get('soloHijos')),
      mes: parseInt(searchParams.get('mes')),
      anio: parseInt(searchParams.get('anio')),
    });
    setCostCentreTotalsRequest(() =>
      coreService
        .getCostCentreTotals(params)
        .then((response) => {
          return response;
        })
        .catch((err) => {
          const message = new SearchCostCentreTotalsError(err.error).message;
          addNotification(message, 'error');
          navigate(goBackPath);
          return null;
        }),
    );
  }, [
    navigate,
    coreService,
    ccId,
    searchParams,
    addNotification,
    goBackPath,
    setCostCentreTotalsRequest,
  ]);

  const value = {
    costCentreTotals: costCentreTotals.data,
    searchParams,
    period,
    goBack: () => {
      navigate(goBackPath, {
        state: {
          selectedCC: ccId,
        },
      });
    },
    goToMovementsByCC: () => {
      if (costCentreTotals && costCentreTotals.data.esCentroCostoRaiz) {
        navigate(
          {
            pathname: CARDS_TOTALS_BY_ACCOUNT_PATH.replace(
              ':accountNumber',
              costCentreTotals.data.numeroCuenta,
            ),
            search: searchParams.toString(),
          },
          {
            state: {
              fromCostCentreTotals: ccId,
            },
          },
        );
      }
      return;
    },
    currentCostCenterId: () => {
      return Number.parseInt(ccId);
    },
    handleCostCenterRedirection: () => {
      const { totales } = costCentreTotals.data;
      if (!totales.tarjetas && !totales.hijos) {
        addNotification(COST_CENTRE_MESSAGES.notCardsOrChildren, 'error');
        return;
      }
      if (totales.hijos > 0) {
        navigate({
          pathname: COST_CENTRE_TOTALS_BY_ID_PATH.replace(
            ':ccId',
            `${totales.centroCosto.id}`,
          ),
          search: `${formatParams(searchParams)}&soloHijos=true`,
        });
        return;
      }
      if (totales.tarjetas > 0) {
        navigate(
          {
            pathname: CARDS_TOTALS_BY_ACCOUNT_PATH.replace(
              ':accountNumber',
              `${costCentreTotals.data.numeroCuenta}`,
            ),
            search: formatParams(searchParams),
          },
          {
            state: {
              fromCostCentreTotals: totales.centroCosto.id,
            },
          },
        );
      }
    },
  };

  return (
    <CostCentreTotalsContext.Provider value={value}>
      <LoadingSpinner isLoading={costCentreTotals.isLoading}>
        {costCentreTotals.data && children}
      </LoadingSpinner>
    </CostCentreTotalsContext.Provider>
  );
};
